'use client'

import { openLogin } from '@/components/auth/open-auth'
import * as TabsPrimitive from '@radix-ui/react-tabs'
import { ArrowRight, Globe } from 'lucide-react'
import Image from 'next/image'
import { useState } from 'react'

import { ImageHelper } from '@/lib/helpers/image.helper'
import { cn } from '@/lib/utils'
import { GuideExample, GuideSteps } from './guide-steps'
import { PlatformGuideDialog } from './platform-guide-dialog'
import {
  GUIDE_PLATFORMS,
  GuidePlatform,
  PLATFORM_GUIDES,
  PLATFORM_TAB_LABEL,
  PlatformGuide,
} from './platform-guide.config'

interface OrderCodeGuideCardProps {
  platform: GuidePlatform
  onPlatformChange: (platform: GuidePlatform) => void
  isLoggedIn: boolean
  className?: string
}

function PlatformIcon({ platform }: { platform: GuidePlatform }) {
  if (platform === 'web') {
    return <Globe className='size-[18px] md:size-6' strokeWidth={2} />
  }

  return (
    <Image
      src={ImageHelper.getImageUrl(PLATFORM_GUIDES[platform].iconPath!)}
      alt=''
      width={24}
      height={24}
      className='size-[18px] object-contain md:size-6'
    />
  )
}

function WebGuide({ isLoggedIn }: { isLoggedIn: boolean }) {
  if (isLoggedIn) {
    return (
      <p className='text-sm text-neutral-700'>
        Hệ thống tự động tìm và tải danh sách đơn hàng đã mua ở dưới phần{' '}
        <strong className='font-bold'>đơn hàng gần đây.</strong>
      </p>
    )
  }

  return (
    <div className='space-y-2 text-sm text-neutral-700'>
      <p>
        Bạn có tài khoản Coolmate?
        <button
          type='button'
          onClick={() => openLogin()}
          className='ml-4 text-primary-700 underline underline-offset-2'
        >
          Đăng nhập ngay
        </button>
      </p>
      <p>
        Đăng nhập ngay để hệ thống tự động tìm và tải danh sách đơn hàng đã mua
        nhanh nhất.
      </p>
    </div>
  )
}

function MarketplaceGuide({
  guide,
  onShowDetail,
}: {
  guide: PlatformGuide
  onShowDetail: () => void
}) {
  return (
    <div className='flex flex-col gap-2 md:gap-4'>
      <GuideSteps steps={guide.steps} />
      <GuideExample text={guide.exampleText} code={guide.exampleCode} />
      <button
        type='button'
        onClick={onShowDetail}
        className='flex w-fit items-center gap-2 text-sm text-primary-700'
      >
        Xem hướng dẫn chi tiết
        <ArrowRight className='size-5' strokeWidth={1.5} />
      </button>
    </div>
  )
}

export function OrderCodeGuideCard({
  platform,
  onPlatformChange,
  isLoggedIn,
  className,
}: OrderCodeGuideCardProps) {
  const [detailGuide, setDetailGuide] = useState<PlatformGuide | null>(null)

  return (
    <section className={className}>
      <h2 className='font-criteria text-lg tracking-[-0.02em] text-neutral-900'>
        Hướng dẫn lấy mã đơn hàng
      </h2>

      <TabsPrimitive.Root
        value={platform}
        onValueChange={(value) => onPlatformChange(value as GuidePlatform)}
        className='mt-4 md:mt-6'
      >
        <TabsPrimitive.List className='grid grid-cols-4 gap-1 md:flex md:gap-3'>
          {GUIDE_PLATFORMS.map((value) => (
            <TabsPrimitive.Trigger
              key={value}
              value={value}
              className={cn(
                'flex flex-col items-center justify-center gap-1 rounded-md bg-neutral-200 px-1 py-2 font-criteria text-[10px] leading-3 text-neutral-900 transition-colors',
                'md:flex-auto md:flex-row md:gap-2 md:rounded-full md:px-6 md:py-2 md:text-base md:leading-6',
                'data-[state=active]:bg-neutral-900 data-[state=active]:text-white'
              )}
            >
              <PlatformIcon platform={value} />
              {PLATFORM_TAB_LABEL[value]}
            </TabsPrimitive.Trigger>
          ))}
        </TabsPrimitive.List>

        <TabsPrimitive.Content value='web' className='mt-2 md:mt-4'>
          <WebGuide isLoggedIn={isLoggedIn} />
        </TabsPrimitive.Content>

        {(['shopee', 'tiktok', 'lazada'] as const).map((value) => (
          <TabsPrimitive.Content key={value} value={value} className='mt-4'>
            <MarketplaceGuide
              guide={PLATFORM_GUIDES[value]}
              onShowDetail={() => setDetailGuide(PLATFORM_GUIDES[value])}
            />
          </TabsPrimitive.Content>
        ))}
      </TabsPrimitive.Root>

      <PlatformGuideDialog
        guide={detailGuide}
        onOpenChange={(open) => !open && setDetailGuide(null)}
      />
    </section>
  )
}
